import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_ITEMS, SOCIALS, LINKS } from "@/data/content";
import { LogoMark, SocialIcon } from "@/components/Icons";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
          scrolled
            ? "bg-cream/90 backdrop-blur-md border-line shadow-[0_8px_30px_rgba(44,42,41,0.05)]"
            : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 group"
            aria-label="Laure Hila — Home"
          >
            <LogoMark size={34} />
            <span className="leading-tight">
              <span className="block font-serif text-lg md:text-xl font-semibold text-ink group-hover:text-gold transition-colors duration-300">
                Laure Hila
              </span>
              <span className="block text-[10px] uppercase tracking-[0.28em] text-stone">
                Viaggia in Grande
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Navigazione principale">
            {NAV_ITEMS.filter((n) =>
              ["/la-mia-storia", "/travel-advantage", "/un-nuovo-modo-di-lavorare", "/viaggi", "/skal-albania", "/blog"].includes(n.path)
            ).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, "") || "home"}-link`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide link-underline transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-ink hover:text-gold"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contatti"
              data-testid="nav-contatti-cta"
              className="text-sm font-semibold px-5 py-2.5 rounded-full bg-ink text-cream hover:bg-gold transition-colors duration-300"
            >
              Contatti
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            className="lg:hidden p-2 text-ink hover:text-gold transition-colors duration-300"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-40 bg-cream grain flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex-1 overflow-y-auto pt-24 pb-10 px-7 flex flex-col">
              <nav className="flex flex-col gap-1" aria-label="Menu mobile">
                {NAV_ITEMS.map((item, i) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <NavLink
                      to={item.path}
                      data-testid={`mobile-nav-${item.path.replace(/\//g, "") || "home"}-link`}
                      className={({ isActive }) =>
                        `block py-2.5 font-serif text-3xl font-medium transition-colors duration-300 ${
                          isActive ? "text-gold italic" : "text-ink hover:text-gold"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <motion.div
                className="mt-auto pt-10"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
              >
                <a
                  href={LINKS.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-menu-whatsapp"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-cream text-sm font-semibold hover:bg-gold transition-colors duration-300"
                >
                  Parliamo su WhatsApp
                </a>
                <div className="flex items-center gap-5 mt-7">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      data-testid={`mobile-social-${s.icon}`}
                      className="text-stone hover:text-gold transition-colors duration-300"
                    >
                      <SocialIcon icon={s.icon} size={20} />
                    </a>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
